import React from "react";
import { X, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useNavigate } from "react-router-dom";
import { resolveMediaUrl } from "../lib/api";

const CartDrawer = () => {
  const { items, subtotal, isOpen, setIsOpen, updateQty, removeItem, count } = useCart();
  const { formatPrice, currency } = useCurrency();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const goCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <div data-testid="cart-drawer" className="fixed inset-0 z-[70]">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
        data-testid="cart-drawer-backdrop"
      />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-white/10 flex flex-col">
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[10px]">Écrin</p>
            <h2 className="font-serif text-2xl text-white">Votre sélection ({count})</h2>
          </div>
          <button data-testid="cart-close-btn" onClick={() => setIsOpen(false)} className="text-white/60 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-4">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/50 font-light">Votre écrin est vide.</p>
              <button
                data-testid="cart-continue-shopping"
                onClick={() => setIsOpen(false)}
                className="mt-6 label-caps text-[color:var(--gold)] border-b border-[color:var(--gold)] pb-1"
              >
                Découvrir la collection
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((it) => (
                <li key={it.key} data-testid={`cart-item-${it.slug}`} className="flex gap-4 py-6">
                  <img src={resolveMediaUrl(it.image)} alt={it.name} className="w-24 h-32 object-cover" />
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-serif text-lg text-white">{it.name}</h3>
                        <p className="label-caps text-white/40 text-[10px] mt-1">{it.volume}</p>
                      </div>
                      <p className="font-serif text-[color:var(--gold)]">{formatPrice(it.price * it.quantity, { decimals: 0 })}</p>
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-white/15">
                        <button data-testid={`cart-decrease-${it.slug}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2 text-white/70 hover:text-[color:var(--gold)]"><Minus size={12} /></button>
                        <span className="px-3 text-sm text-white">{it.quantity}</span>
                        <button data-testid={`cart-increase-${it.slug}`} onClick={() => updateQty(it.key, it.quantity + 1)} className="p-2 text-white/70 hover:text-[color:var(--gold)]"><Plus size={12} /></button>
                      </div>
                      <button data-testid={`cart-remove-${it.slug}`} onClick={() => removeItem(it.key)} className="label-caps text-white/40 hover:text-white text-[10px]">Retirer</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 px-8 py-6">
            <div className="flex items-center justify-between mb-2 text-white/60 text-sm">
              <span>Sous-total</span>
              <span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <p className="text-xs text-white/40 mb-6">Livraison offerte dès 60€ en France (dès 160€ à l'international). Retours 14 jours.{currency !== "EUR" && <span className="block mt-1 text-[color:var(--gold)]/70">Paiement facturé en EUR (devise de référence).</span>}</p>
            <button
              data-testid="cart-checkout-btn"
              onClick={goCheckout}
              className="btn-gold w-full"
            >
              Passer la commande <ArrowRight size={14} />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
